// Exam.jsx
import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import useFetch from '../../../hooks/useFetch';
import '../styles/MCQPractice.css';
import Think5WhysCertificateImage from '../../../certificates/Think5WhysCertTemplateAug24.png';
import OtherCertificateImage from '../../../certificates/Think5WhysCertTemplateDec24.png';
import { logout } from "../../../redux/actions/authActions";

const Exam = () => {
    const authState = useSelector(state => state.authReducer);
    const [fetchData, { loading }] = useFetch();
    const navigate = useNavigate();
    const location = useLocation();

    const isLoggedIn = authState?.isLoggedIn || false;
    const userName = authState?.user?.name || "User";

    const dispatch = useDispatch();

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const handleLogout = () => {
        dispatch(logout());
        navigate("/");
    };

    useEffect(() => {
        // Important - useEffect to stop infinite rendering
    }, [location.state.setId]);

    const [setId, setSetId] = useState(location.state.setId);
    const [isT5w, setIsT5w] = useState(location.state.isT5w);
    const [setTitle, setSetTitle] = useState(location.state.setTitle);
    const [seriesId, setSeriesId] = useState(location.state.seriesId);
    const [seriesTitle, setSeriesTitle] = useState(location.state.seriesTitle);
    const [examId, setExamId] = useState(location.state.examId);
    const [examTitle, setExamTitle] = useState(location.state.examTitle);
    const [examType, setType] = useState(location.state.examType);
    const [timeLimitInMin, setTimeLimitInMin] = useState(location.state.timeLimitInMin);
    const [isRepeatExam, setIsRepeatExam] = useState(location.state.isRepeatExam);

    const [repeatExamId, setRepeatExamId] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [timer, setTimer] = useState(0);
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [answers, setAnswers] = useState([]);
    const [certificate, setCertificate] = useState([]);
    const [submitted, setSubmitted] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState(Array(questions.length).fill(null));

    const [savedToDB, setSavedToDB] = useState(false);

    const [totalCorrectAnswers, setTotalCorrectAnswers] = useState(0);
    const [fullMarks, setFullMarks] = useState(0);
    const [totalMarksObtained, setTotalMarksObtained] = useState(0);
    const [percentage, setPercentage] = useState(0);
    const [certificateNumber, setCertificateNumber] = useState(null);
    
    const [certificateSetup, setCertificateSetup] = useState(null);

    useEffect(() => {
        if (!authState.isLoggedIn) return;
        fetchQuestions();
    }, [authState]);


    const fetchQuestions = useCallback(() => {
        if (!setId) return;
        const config = {
            url: `/questionmaster/forsetid`,
            method: "post",
            headers: { Authorization: authState.token },
            data: {setId: setId}
        };
        fetchData(config, { showSuccessToast: false })
            .then(response => {
                // console.log("Question List Response:", response);
                if (response && Array.isArray(response.questionlist)) {
                    setQuestions(response.questionlist);
                    getCertificateSetup(examId);
                } else {
                    console.error("Invalid response format: Assignment not found in response");
                }
            })
            .catch(error => {
                console.error("Error fetching Assignments:", error);
            });
    }, [fetchData, setId]);

    useEffect(() => {
        // console.log("Questions: ", questions);
    }, [questions]);

    useEffect(() => {
        if (submitted) {
            return;
        }
        setStartTime(new Date());
        const intervalId = setInterval(() => {
            setTimer(prevTimer => prevTimer + 1);
        }, 1000);

        return () => clearInterval(intervalId);
    }, [submitted]);

    const allQuestionsAnswered = selectedOptions.every(option => option !== null);

    const handleOptionChange = (index, option) => {
        const newSelectedOptions = [...selectedOptions];
        newSelectedOptions[index] = option;
        setSelectedOptions(newSelectedOptions);
    };

    const [answeredAllBeforeSubmit, setAnsweredAllBeforeSubmit] = useState(false);
    const [answerAllBeforeSubmitMsg, setAnswerAllBeforeSubmitMsg] = useState(null);

    const handleSubmit = () => {
        if (submitted) {
            return;
        }
        const answeredQuestions = selectedOptions.filter(option => option !== null);
        if (answeredQuestions.length !== questions.length) {
            setAnswerAllBeforeSubmitMsg("Complete your exam. Give all the answers before submitting it.");
            return;
        }
        setAnsweredAllBeforeSubmit(true);
        setEndTime(new Date());
        setSubmitted(true);
        showAnswers();
    };

    const generateCertificateNumber = () => {
        const currentDate = new Date();
        const certificateNumber = currentDate.getFullYear().toString().padStart(4, '0') + // Year
            (currentDate.getMonth() + 1).toString().padStart(2, '0') + // Month (zero-based)
            currentDate.getDate().toString().padStart(2, '0') + // Day
            currentDate.getHours().toString().padStart(2, '0') + // Hours
            currentDate.getMinutes().toString().padStart(2, '0') + // Minutes
            currentDate.getSeconds().toString().padStart(2, '0') + // Seconds
            currentDate.getMilliseconds().toString().padStart(3, '0'); // Milliseconds
            setCertificateNumber(certificateNumber);
    }

    const getCertificateSetup = async (examId) => {
        const config = {
            url: "/myexamsets/certificatesetup",
            method: "post",
            headers: { Authorization: authState.token },
            data: { examId: examId },
        };
    
        // console.log("getCertificateDetails config: ", config);
    
        try {
            fetchData(config, { showSuccessToast: false })
                .then((response) => {
                    // console.log("getCertificateDetails response: ", response);

                    if (response && response.certificateSetup) {
                        setCertificateSetup(response.certificateSetup);
                        setCertificate({
                            certificateNumber: certificateNumber, 
                            certificateDate: new Date(),
                            certificateName: authState.user.name,
                            certificateTitle: certificateSetup.certificateTitle,
                            certificateDesc: certificateSetup.certificateDesc,
                            certificateTemplate: certificateSetup.certificateTemplate,
                            signName1: certificateSetup.signName1,
                            signDesig1: certificateSetup.signDesig1,
                            signEntity1: certificateSetup.signEntity1,
                            signImage1: certificateSetup.signImage1,
                            isSignatory2: certificateSetup.isSignatory2,
                            signName2: certificateSetup.signName2,
                            signImage2: certificateSetup.signImage2,
                            signDesig2: certificateSetup.signDesig2,
                            signEntity2: certificateSetup.signEntity2,
                            hasSponsor: certificateSetup.hasSponsor,
                            sponsorName: certificateSetup.sponsorName,
                            sponsorLogo: certificateSetup.sponsorLogo,
                            certificateRemarks:
                                "Full Marks: " +
                                fullMarks +
                                " Number of Questions: " +
                                questions.length +
                                " Correct Answer: " +
                                totalCorrectAnswers +
                                " Wrong Answer: " +
                                questions.length-totalCorrectAnswers +
                                " Marks Obtained: " +
                                totalMarksObtained +
                                " Percent: " +
                                percentage +
                                "%",
                        });
                    } else {
                        console.error("Certificate setup not found in response.");
                    }
                })
                .catch((error) => {
                    console.error("Error in fetchData call:", error);
                });
        } catch (error) {
            console.error("Error fetching certificate details:", error);
        }
    };
    
    
    useEffect(() => {
        if (certificateNumber) {
            setCertificate({
                certificateNumber: certificateNumber,
                certificateDate: new Date(),
                certificateName: authState.user.name,
                certificateDesc: examTitle,
                certificateTitle: certificateSetup.certificateTitle,
                certificateDesc: certificateSetup.certificateDesc,
                certificateTemplate: certificateSetup.certificateTemplate,
                signName1: certificateSetup.signName1,
                signDesig1: certificateSetup.signDesig1,
                signEntity1: certificateSetup.signEntity1,
                signImage1: certificateSetup.signImage1,
                isSignatory2: certificateSetup.isSignatory2,
                signName2: certificateSetup.signName2,
                signImage2: certificateSetup.signImage2,
                signDesig2: certificateSetup.signDesig2,
                signEntity2: certificateSetup.signEntity2,
                hasSponsor: certificateSetup.hasSponsor,
                sponsorName: certificateSetup.sponsorName,
                sponsorLogo: certificateSetup.sponsorLogo,
                certificateRemarks: "Marks: " + totalMarksObtained + " / " + fullMarks + " ( " + percentage.toFixed(2) + " % )" +
                                    " [#Questions: " + questions.length + 
                                    " ,Correct Answer: " + totalCorrectAnswers + 
                                    " ,Wrong Answer: " + (questions.length-totalCorrectAnswers) + ']'
            });
        }
        // console.log('Certificate state:', certificate);
    }, [certificateNumber, authState.user.name, examTitle]);
    
    const showAnswers = () => {
        const correctCount = questions.reduce((count, question, index) => {
            const correctAnswer = question.mcqCorrectAnswer;
            const userAnswer = selectedOptions[index];
            if (userAnswer === correctAnswer) {
                return count + 1;
            }
            return count;
        }, 0);

        const totalQuestions = questions.length;

        let totalMarksObtained = 0;
        let fullMarks = 0;
        let totalCorrectAnswers = 0;

        const answeredQuestions = questions.map((question, index) => {
            const correctAnswer = question.mcqCorrectAnswer;
            const userAnswer = selectedOptions[index];
            const isCorrect = userAnswer === correctAnswer;
            const correctAnswerPoints = question.correctAnswerPoints;
            const wrongAnswerPoints = question.wrongAnswerPoints;
            const isCorrectAnswer = isCorrect ? 1 : 0;
            totalCorrectAnswers += isCorrectAnswer;
            const marksObtained = isCorrect ? question.correctAnswerPoints : question.wrongAnswerPoints;
            totalMarksObtained += marksObtained;
            fullMarks += question.correctAnswerPoints;
            return { question, correctAnswer, userAnswer, marksObtained, correctAnswerPoints, wrongAnswerPoints };
        });
        setAnswers(answeredQuestions);
        setTotalCorrectAnswers(totalCorrectAnswers);
        setFullMarks(fullMarks);
        setTotalMarksObtained(totalMarksObtained);
        const calculatedPercentage = (totalMarksObtained / fullMarks) * 100;
        setPercentage(calculatedPercentage);
        generateCertificateNumber();
        setCertificate();
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSaveExam = () => {
        saveExamToDB();
    };

    const saveExamToDB = async () => {
        const userId = authState.user._id;
        const numberOfQuestions = questions.length;
        const totalIncorrectAnswers = numberOfQuestions - totalCorrectAnswers;
        // const certificateDetails = getCertificateDetails(examId);
        const requestData = {
            isT5w: isT5w,
            setQuestionType: "MCQ",
            userId: userId,
            setId: setId,
            setTitle: setTitle,
            seriesId: seriesId,
            seriesTitle: seriesTitle,
            examId: examId,
            examTitle: examTitle,
            examType: examType,
            isRepeatExam: isRepeatExam,
            repeatExamId: repeatExamId,
            numberOfQuestions: numberOfQuestions,
            correctAnswers: totalCorrectAnswers,
            incorrectAnswers: totalIncorrectAnswers,
            dateExam: startTime,
            ExamTimeStarted: startTime,
            ExamTimeEnded: endTime,
            marksObtained: totalMarksObtained,
            percentage: percentage,
            totalMarksObtained: totalMarksObtained,
            fullMarks: fullMarks,
            answers: answers,
            remarks: "",
            tags: "",
            ExamLocation: "",
            mentorName: "",
            mentorRemarks: "",
            selfFeedback: "",
            think5whysRcaId: "",
            locationIP: "",
            hasExplanation: 0,
            isActive: 1,
            isDeleted: 0,
            createBy: userId,
            timeTaken: timer * 1000,
            timeLimit: timeLimitInMin,
            certificate: certificate,
        };
        // console.log('Exam data to be saved:', requestData);
        try {
            const config = { url: `/saveexam`, method: "post", data: requestData, headers: { Authorization: authState.token } };
            await fetchData(config);
            setSavedToDB(true);
        } catch (error) {
            console.error('An error occurred while fetching data:', error);
        }
    };

    const handleViewCertificate = () => {
        drawCertificate();
    };

    // Function to wrap text
    function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
        const words = text.split(' ');
        let line = '';
        const lines = [];

        for (let i = 0; i < words.length; i++) {
            const testLine = line + words[i] + ' ';
            const metrics = ctx.measureText(testLine);
            const testWidth = metrics.width;

            if (testWidth > maxWidth && i > 0) {
                lines.push(line);
                line = words[i] + ' ';
            } else {
                line = testLine;
            }
        }

        lines.push(line);

        // Center text vertically
        const totalHeight = lines.length * lineHeight;
        const yStart = y - totalHeight / 2;

        lines.forEach((line, index) => {
            const lineWidth = ctx.measureText(line).width;
            const xPosition = (canvasWidth - lineWidth) / 2; // Center horizontally
            ctx.fillText(line, xPosition, yStart + (index * lineHeight));
        });
    }

    const drawCertificate = () => {
        const canvas = document.getElementById('certificateCanvas');
        const ctx = canvas.getContext('2d');
        const canvasHeight = canvas.height;
        const canvasWidth = canvas.width;

        const certificateDate = certificate.certificateDate;
        const formattedDate = certificateDate.getDate().toString().padStart(2, '0') + '-' +
            (certificateDate.getMonth() + 1).toString().padStart(2, '0') + '-' +
            certificateDate.getFullYear();

        let fontSize = 18;
        const textName = authState.user.name;
        const textNameWidth = ctx.measureText(textName).width;
        const xCoordinateName = (canvasWidth - textNameWidth) / 2;
        const adjustedXCoordinateName = xCoordinateName - (textNameWidth / 2) + (fontSize / 2);

        fontSize = 18;
        const textExamTitle = examTitle;
        const textExamTitleWidth = ctx.measureText(textExamTitle).width;
        const xCoordinateExamTitle = (canvasWidth - textExamTitleWidth) / 2;
        const adjustedXCoordinateExamTitle = xCoordinateExamTitle - (textExamTitleWidth / 2) + (fontSize / 2);

        fontSize = 12;
        const lineHeight = fontSize * 1.5; 
        const maxWidth = canvasWidth - 40; 
        ctx.font = `${fontSize}px Arial`;
        const certificateRemarks = "Marks: " + totalMarksObtained + " / " + fullMarks + " ( " + percentage.toFixed(2) + " % )" +
                                    " [#Questions: " + questions.length + 
                                    " ,Correct Answer: " + totalCorrectAnswers + 
                                    " ,Wrong Answer: " + (questions.length-totalCorrectAnswers) + ']'                            
        const certificateRemarksWidth = ctx.measureText(certificateRemarks).width;
        const xCoordinateCertificateRemarks = (canvasWidth - certificateRemarksWidth) / 2;
        const adjustedXCoordinateExamRemarks = xCoordinateCertificateRemarks - (certificateRemarksWidth / 2) + (fontSize / 2);

        const certificateImage = new Image();
        certificateImage.onload = function () {
            ctx.drawImage(certificateImage, 0, 0);
            ctx.fillStyle = 'black';
            ctx.font = '12px Arial';
            ctx.fillText(certificate.certificateNumber, 480, 35);
            ctx.font = '18px Arial';
            ctx.fillText(textName, adjustedXCoordinateName, 260);
            ctx.font = '18px Arial';
            ctx.fillText(textExamTitle, adjustedXCoordinateExamTitle, 290);
            // const yCoordinate = 340;
            // wrapText(ctx, certificateRemarks, canvasWidth / 2, yCoordinate, maxWidth, lineHeight);
            ctx.font = '12px Arial';
            ctx.fillText(certificateRemarks, 165, 340);

            ctx.font = '12px Arial';
            ctx.fillText(formattedDate, 165, 430);
        };
        if(isT5w === 1) {
            certificateImage.src = Think5WhysCertificateImage; 
        } else {
            certificateImage.src = OtherCertificateImage;  
        }
    };

    const handleClose = () => {
        navigate('/certification-test-think5whys');
    };

    const getOptionValue = (options, key) => {
        return options[key];
    };

    const isSubmitDisabled = selectedOptions.includes(null);

    const formatTime = (timeInSeconds) => {
        const hours = Math.floor(timeInSeconds / 3600);
        const minutes = Math.floor((timeInSeconds % 3600) / 60);
        const seconds = timeInSeconds % 60;

        return `${hours}Hr ${minutes}Min ${seconds}Sec`;
    };

    return (
        <div className="min-h-screen flex flex-col bg-black text-white font-sans">
            <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-2 flex items-center justify-between shadow-lg">
                <div className="flex flex-col">
                    <div className="text-m lg:text-xl font-bold text-yellow-200">✨Certification Exams / Assessemnt</div>
                </div>

                <div className="hidden md:flex items-left gap-3">
                    {isLoggedIn ? (
                        <>
                        <div className="text-xs text-left text-white">{userName}</div>
                        <button
                            onClick={handleLogout}
                            className="p-2 rounded-md bg-cyan-600 hover:bg-cyan-700 transition"
                            title="Logout"
                        >
                            ⏻
                        </button>
                        </>
                    ) : (
                        <>
                        <button
                            onClick={() => navigate("/login")}
                            className="px-3 py-1 rounded-full border border-white-700 text-sm text-white hover:bg-white-900"
                        >
                            Login
                        </button>
                        </>
                    )}
                </div>

                <button
                    onClick={() => setIsMobileMenuOpen((s) => !s)}
                    className="md:hidden w-8 h-8 p-1 flex items-center justify-center text-xl text-white bg-white-500"
                    aria-label="Toggle menu"
                    >
                    {isMobileMenuOpen ? "×" : "☰"}
                </button>
            </header>

            <div className="flex flex-col justify-center items ml-4 mr-4 mx-auto mt-2 mb-2 p-2 bg-black border border-blue-800 rounded-lg shadow-lg">
                <p className='text-white text-sm lg:text-m'>Welcome, {authState.user && authState.user.name ? authState.user.name : 'Guest'}</p>
                <p className='text-white text-sm lg:text-m'>Exam: {examTitle}-{examType}</p>
                <p className='text-white text-sm lg:text-m'>Number of Questions: {questions.length}</p>               
                <p className='text-white text-sm lg:text-m'>Your Exam Begins at: {startTime && startTime.toLocaleString()}</p>
                <p className='text-yellow-300 text-sm lg:text-m'>Time Limit: {timeLimitInMin} Minutes</p>
                <p className='text-white text-sm lg:text-m'>Time Taken: {formatTime(timer)}</p>
                {questions.length<1 && (
                    <>
                        <p className='mt-8 mb-8 text-xl text-orange-400'>Questions are not setup yet for {examTitle}!!</p>
                    </>
                )}
            </div>
            {questions.map((question, index) => (
                <div key={question._id} className="question-item bg-gray-700 p-4 mb-4 rounded-md shadow-md mx-4">
                    <p className="question-number text-white">Question: {index + 1}
                        <span className="text-gray-500 ml-4">
                            [Correct Answer : {question.correctAnswerPoints} marks, Wrong Answer: {question.wrongAnswerPoints} marks]
                        </span>
                    </p>
                    <p className="question-text text-white">Question: {question.question}</p>
                    <form>
                        <div>
                            <input
                                type="radio"
                                id={`${question._id}-A`}
                                name={`question_${index}`}
                                value="A"
                                onChange={() => handleOptionChange(index, 'A')}
                            />
                            <label htmlFor={`${question._id}-A`}>{question.mcqOptions[0].A}</label>
                        </div>
                        <div>
                            <input
                                type="radio"
                                id={`${question._id}-B`}
                                name={`question_${index}`}
                                value="B"
                                onChange={() => handleOptionChange(index, 'B')}
                            />
                            <label htmlFor={`${question._id}-B`}>{question.mcqOptions[0].B}</label>
                        </div>
                        <div>
                            <input
                                type="radio"
                                id={`${question._id}-C`}
                                name={`question_${index}`}
                                value="C"
                                onChange={() => handleOptionChange(index, 'C')}
                            />
                            <label htmlFor={`${question._id}-C`}>{question.mcqOptions[0].C}</label>
                        </div>
                        <div>
                            <input
                                type="radio"
                                id={`${question._id}-D`}
                                name={`question_${index}`}
                                value="D"
                                onChange={() => handleOptionChange(index, 'D')}
                            />
                            <label htmlFor={`${question._id}-D`}>{question.mcqOptions[0].D}</label>
                        </div>
                    </form>
                </div>
            ))}
            { !answeredAllBeforeSubmit && (
                <div className="text-red-600">
                    {answerAllBeforeSubmitMsg}
                </div>
            )}
            <button
                className="save-practice-button"
                onClick={handleSubmit}
                // disabled={!allQuestionsAnswered }
                disabled={questions.length<1 }

            >
                Submit
            </button>
            {submitted && answers.length > 0 && (
                <div className="submission-details text-white mb-4">
                    <p>{authState.user && authState.user.name ? authState.user.name : 'Guest'} Your Exam Is Over.</p>
                    <p>Start Time: {startTime && startTime.toLocaleString()}</p>
                    <p>End Time: {endTime && endTime.toLocaleString()}</p>
                    <p>Time Taken: {formatTime(timer)}</p>
                </div>
            )}
            {submitted && (
                <button
                    className="save-practice-button"
                    onClick={handleSaveExam}
                    disabled={savedToDB}
                >
                    View Result
                </button>
            )}
            {submitted && savedToDB && (
                <div className="additional-stats text-yellow-300">
                    <p>Number of Questions: {questions.length}</p>
                    <p>Correct Answers: {answers.filter(answer => answer.userAnswer === answer.correctAnswer).length}</p>
                    <p>Incorrect Answers: {answers.filter(answer => answer.userAnswer !== answer.correctAnswer).length}</p>
                    <p>Full Marks: {fullMarks}</p>
                    <p>Marks Obtained: {totalMarksObtained}</p>
                    <p>Percent: {percentage}%</p>
                    <br />
                </div>
            )}
            {submitted && savedToDB && (
                <div className="progress-container">
                    <div className="progress-bar">
                        <div className="progress" style={{ width: `${percentage}%` }}>
                            {`${percentage}%`}
                        </div>
                    </div>
                </div>
            )}
                {/* {submitted && savedToDB  && answers.map((answer, index) => (
                    <div key={index} className="answer-item text-white">
                        <p className="question-info text-yellow-300">Question {index + 1}: {answer.question.question}</p>
                        <p className="user-answer">
                            Your Answer:
                            <span className={answer.userAnswer === answer.correctAnswer ? 'correct-answer' : 'incorrect-answer'}>
                                {answer.userAnswer} ({getOptionValue(answer.question.mcqOptions[0], answer.userAnswer)})
                            </span>
                        </p>
                        {answer.userAnswer !== answer.correctAnswer && (
                            <p className="correct-answer">
                                Correct Answer: <span className="correct-answer-text">
                                    {answer.correctAnswer} ({getOptionValue(answer.question.mcqOptions[0], answer.correctAnswer)})
                                </span>
                            </p>
                        )}
                        <p className="question-info">Marks Obtained: {answer.marksObtained}</p>
                        <p className="question-info text-gray-400">[Marks for correct answer: <strong>{answer.correctAnswerPoints}</strong>, Marks for wrong answer: <strong>{answer.wrongAnswerPoints}]</strong></p>
                    </div>
                ))} */}
            {submitted && savedToDB && (
                <div className='flex flex-col justify-center mt-4'>
                    <button className="flex items-center save-practice-button bg-purple-600" onClick={handleViewCertificate}>View Certificate</button>
                    <canvas className="flex items-center" id="certificateCanvas" width="800" height="600"></canvas>
                </div>
            )}
            {submitted && savedToDB && (
                <button className="save-practice-button" onClick={handleClose}>Close</button>
            )}
        {/* </div> */}
        <footer className="bg-black border-t border-blue-400 text-white py-4">
            <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
                <div className="text-sm text-yellow-200">© 2025 Think5Whys Global DeepTech Solutions.</div>
                <div className="flex flex-wrap gap-4 text-sm">
                    <Link to="/about-us" className="hover:text-teal-300">About</Link>
                    <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
                    <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
                    <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
                </div>
            </div>
        </footer>        
        </div>
    );
};

export default Exam;
